import { createContext, useContext, useLayoutEffect, useState, useRef, useCallback, type ReactNode } from "react";
import { DEFAULT_PROJECT_THEME, canonicalThemeName, getProjectThemePreset, normalizeProjectTheme, projectThemeCssVariables, type ProjectTheme } from "@private-fiction/story-core";

const KEY = "mvpstory:app-appearance:v1";
const AppearanceContext = createContext({ theme: DEFAULT_PROJECT_THEME, setTheme: (_: ProjectTheme) => {}, setScope: (_: string) => {} });
export function AppAppearanceProvider({ children }: { children: ReactNode }) {
  const scope = useRef("local");
  const [theme, setTheme] = useState(() => {
    try { const saved = JSON.parse(localStorage.getItem(`${KEY}:local`) || "null"); return saved?.presetId === "custom" ? normalizeProjectTheme(saved) : getProjectThemePreset(saved?.presetId || DEFAULT_PROJECT_THEME.presetId); }
    catch { return DEFAULT_PROJECT_THEME; }
  });
  const setScope = useCallback((next: string) => {
    if (scope.current === next) return;
    scope.current = next;
    try { const saved = JSON.parse(localStorage.getItem(`${KEY}:${next}`) || 'null'); setTheme(saved?.presetId === 'custom' ? normalizeProjectTheme(saved) : getProjectThemePreset(saved?.presetId || DEFAULT_PROJECT_THEME.presetId)); }
    catch { setTheme(DEFAULT_PROJECT_THEME); }
  }, []);
  useLayoutEffect(() => {
    const normalized = normalizeProjectTheme(theme);
    const cssVars = projectThemeCssVariables(normalized);
    for (const [key, value] of Object.entries(cssVars)) document.documentElement.style.setProperty(key, value);
    const themeName = canonicalThemeName(normalized.presetId);
    document.documentElement.dataset.theme = themeName;
    document.documentElement.style.colorScheme = normalized.baseMode;
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta && cssVars["--flow-0"]) meta.setAttribute("content", cssVars["--flow-0"]);
    try { localStorage.setItem(`${KEY}:${scope.current}`, JSON.stringify(normalized)); } catch { /* Theme remains usable in memory. */ }
  }, [theme]);
  return <AppearanceContext.Provider value={{ theme, setTheme, setScope }}>{children}</AppearanceContext.Provider>;
}
export const useAppAppearance = () => useContext(AppearanceContext);
